/**
 * Выбор темы в шапке раздела рядом с кнопкой «Темы» (#2229, спека §2.5):
 * «Все темы ▾» и темы набора выше порога. Тема живёт в адресе (`voiceTheme`),
 * как фильтр периода, и смена вкладки её не сбрасывает. Читает тот же ответ
 * блока тем, что вкладка (`useVoiceThemeFilter`, срез вкладки) — второго
 * запроса нет.
 */
import { useTranslation } from 'react-i18next';
import type { VoiceThemeCuts } from '@aica/api-types/schemas';
import { Select } from '@/components/ui/select';
import { useVoiceThemeFilter } from './useVoiceThemeFilter';
import type { VoiceSearch } from './voice-search';

/** The «all themes» option — not a code: a theme code starts with a letter. */
const ALL_THEMES = '_all';

interface VoiceThemeFilterPickerProps {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  /**
   * The open tab's cut of the themes block — its answer is already asked;
   * none on Marketing and Product and service (the rows alone).
   */
  cut: VoiceThemeCuts | undefined;
  onChange: (voiceTheme: string | undefined) => void;
}

export function VoiceThemeFilterPicker({
  search,
  onSearchChange,
  cut,
  onChange,
}: VoiceThemeFilterPickerProps) {
  const { t } = useTranslation('dashboard');
  const filter = useVoiceThemeFilter(search, onSearchChange, cut, { keepPrevious: true });
  if (!filter.shown) return null;
  const value = search.voiceTheme;
  return (
    <Select
      size="sm"
      aria-label={t('voicePage.themeFilter.label')}
      data-testid="voice-theme-filter"
      className="w-56"
      value={value ?? ALL_THEMES}
      onValueChange={(next) => {
        const code = typeof next === 'string' && next !== ALL_THEMES ? next : undefined;
        if (code !== value) onChange(code);
      }}
      options={[
        { value: ALL_THEMES, label: t('voicePage.themeFilter.all') },
        ...filter.options.map((option) => ({ value: option.code, label: option.name })),
      ]}
    />
  );
}
